import { getEditorMarkdown, getEditor } from './editor.js';
import { updateCrdMeta } from './crd-meta.js';
import { updateVersionBadge } from './version-badge.js';
import { updateSectionNavCompletion } from './section-nav.js';

let currentMeta = null;
let onMetaChangedCallback = null;
let checkboxMonitorInterval = null;

// Section IDs that match the meta keys
const SECTION_IDS = [
  'requirements',
  'design_specs',
  'cms',
  'dev_guidance',
  'docs',
  'checklist'
];

// Pre-dev sections that must be complete before ready_for_dev
const PRE_DEV_SECTIONS = [
  'requirements',
  'design_specs',
  'a11y_approval',
  'cms',
  'dev_guidance'
];

/**
 * Initialize section completion tracking
 * @param {Object} meta - Current CRD metadata
 * @param {Function} onMetaChanged - Callback when meta changes (receives updated meta)
 */
export function initSectionComplete(meta, onMetaChanged) {
  const DEBUG = window.DEBUG_FILE_OPEN || false;
  
  if (DEBUG) console.log('[DEBUG] [section-complete] initSectionComplete called');
  
  currentMeta = meta;
  onMetaChangedCallback = onMetaChanged;
  
  // Clear any existing monitor
  if (checkboxMonitorInterval) {
    clearInterval(checkboxMonitorInterval);
  }
  
  // Monitor checkbox changes every 500ms
  checkboxMonitorInterval = setInterval(() => {
    checkCheckboxStates();
  }, 500);
  
  console.log('🎯 Section completion tracking initialized');
  
  if (DEBUG) {
    console.log('[DEBUG] [section-complete] Checkbox monitor started');
    const ec = document.getElementById('editorContainer');
    const ea = document.getElementById('editorArea');
    console.log('[DEBUG] [section-complete] After init - editorContainer display:', ec ? window.getComputedStyle(ec).display : 'NOT FOUND');
    console.log('[DEBUG] [section-complete] After init - editorArea display:', ea ? window.getComputedStyle(ea).display : 'NOT FOUND');
  }
}



/**
 * Check checkbox states and sync with metadata
 */
function checkCheckboxStates() {
  if (!currentMeta) return;
  
  const markdown = getEditorMarkdown();
  if (!markdown) return;
  
  // Check if all pre-dev sections are complete
  const allPreDevComplete = PRE_DEV_SECTIONS.every(
    sectionId => currentMeta.sections[sectionId] === 'complete'
  );
  
  // Map checkbox text to meta keys
  // Support multiple variations of checkbox text for flexibility
  const textToMetaKey = {
    'requirements section complete': 'requirements',
    'design specifications section complete': 'design_specs',
    'accessibility review complete': 'a11y_approval',
    'cms theming considerations section complete': 'cms',
    'cms behaviour section complete': 'cms',  // Alternative text for CMS section
    'cms section complete': 'cms',  // Shorter variation
    'development guidance section complete': 'dev_guidance',
    'dev guidance section complete': 'dev_guidance',  // Shorter variation
    'documentation guidance section complete': 'docs',
    'docs section complete': 'docs',  // Shorter variation
    'completion checklist section complete': 'checklist',
    'checklist section complete': 'checklist'  // Shorter variation
  };
  
  // Find all checkboxes: matches "- [x]" or "- [ ]" followed by text
  const checkboxRegex = /- \[([ xX])\] (.+)/g;
  let match;
  let hasChanges = false;
  
  while ((match = checkboxRegex.exec(markdown)) !== null) {
    const isChecked = match[1].toLowerCase() === 'x';
    const text = match[2].trim().toLowerCase();
    
    // Check if this checkbox text maps to a section
    const metaKey = textToMetaKey[text];
    if (metaKey) {
      const newStatus = isChecked ? 'complete' : 'not_started';
      
      if (currentMeta.sections[metaKey] !== newStatus) {
        currentMeta.sections[metaKey] = newStatus;
        hasChanges = true;
        console.log(`✅ Section ${metaKey} marked as ${newStatus}`);
      }
    }
  }
  
  if (hasChanges) {
    // Check if all sections are complete
    const allComplete = SECTION_IDS.every(
      sectionId => currentMeta.sections[sectionId] === 'complete'
    );
    
    // Notify parent of meta changes
    if (onMetaChangedCallback) {
      onMetaChangedCallback(currentMeta);
    }
    
    // Update section nav completion styling
    updateSectionNavCompletion();
    
    // If all sections complete, show message
    if (allComplete) {
      // Import showToast dynamically to avoid circular dependency
      import('./files.js').then(({ showToast }) => {
        showToast('All sections complete! Consider publishing a new version.', 'success');
      });
    }
  }
}

/**
 * Update the stored metadata reference
 * @param {Object} meta - Updated metadata
 */
export function updateMeta(meta) {
  currentMeta = meta;
}

/**
 * Clean up when file is closed
 */
export function clearSectionComplete() {
  if (checkboxMonitorInterval) {
    clearInterval(checkboxMonitorInterval);
    checkboxMonitorInterval = null;
  }
  currentMeta = null;
  onMetaChangedCallback = null;
  console.log('🎯 Section completion tracking cleared');
}
